<template>
    <div class="base-chips-input" :class="{ 'is-open': isOpen, 'is-disabled': disabled }">
        <label :for="inputId" class="sr-only">{{ label || placeholder }}</label>
        <div ref="field" class="base-chips-input-field form-control" :class="{ 'is-invalid': error }">
            <span v-for="(chip, index) in chips" :key="keyOf(chip)" class="base-chips-input-chip" :class="chipClass">
                {{ labelOf(chip) }}
                <button
                    v-if="!disabled"
                    type="button"
                    class="base-chips-input-remove"
                    :aria-label="$t('delete')"
                    @click.stop="remove(index)"
                >
                    &times;
                </button>
            </span>
            <input
                v-if="!disabled && !isFull"
                :id="inputId"
                ref="input"
                :value="text"
                type="text"
                class="base-chips-input-text"
                autocomplete="off"
                :placeholder="inputPlaceholder"
                :style="{ minWidth: `${inputPlaceholder.length}ch` }"
                @input="type($event.target.value)"
                @focus="focused = true"
                @blur="blur()"
                @keydown.down.prevent="move(1)"
                @keydown.up.prevent="move(-1)"
                @keydown.enter.prevent="enter()"
                @keydown.esc="close()"
            >
        </div>
        <ul v-if="isOpen" class="base-chips-input-menu" :style="menuStyle">
            <li v-if="loading" class="base-chips-input-option is-loading">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </li>
            <template v-else>
                <li
                    v-for="(option, index) in options"
                    :key="option.create ? `create-${option.label}` : keyOf(option.item)"
                    class="base-chips-input-option"
                    :class="{ 'is-highlighted': index === highlighted, 'is-create': option.create }"
                    @mousedown.prevent="pick(index)"
                    @mouseenter="highlighted = index"
                >
                    <slot name="option" :option="option">
                        {{ option.create ? $t('create-chip', { value: option.label }) : option.label }}
                    </slot>
                </li>
            </template>
        </ul>
        <div v-if="error" class="invalid-feedback d-block text-left">{{ error }}</div>
        <small v-else-if="hint && !disabled" class="form-text text-muted text-left">{{ hint }}</small>
    </div>
</template>

<script>
const MAX_OPTIONS     = 8;
const MENU_GAP        = 4;
const MENU_MAX_HEIGHT = 240;

let uid = 0;

export default {
    name:  'base-chips-input',
    props: {
        value: {
            type:    Array,
            default: () => []
        },
        suggestions: {
            type:    Array,
            default: () => []
        },
        placeholder: {
            type:    String,
            default: ''
        },
        addPlaceholder: {
            type:    String,
            default: ''
        },
        label: {
            type:    String,
            default: ''
        },
        hint: {
            type:    String,
            default: ''
        },
        allowCreate: {
            type:    Boolean,
            default: true
        },
        createFirst: {
            type:    Boolean,
            default: true
        },
        disabled: {
            type:    Boolean,
            default: false
        },
        itemLabel: {
            type:    Function,
            default: item => item
        },
        itemKey: {
            type:    Function,
            default: null
        },
        parse: {
            type:    Function,
            default: null
        },
        separator: {
            type:    String,
            default: ''
        },
        filter: {
            type:    Boolean,
            default: true
        },
        loading: {
            type:    Boolean,
            default: false
        },
        commitOnBlur: {
            type:    Boolean,
            default: false
        },
        max: {
            type:    Number,
            default: 0
        },
        chipClass: {
            type:    String,
            default: ''
        }
    },

    data () {
        return {
            inputId:     `base-chips-input-${++uid}`,
            text:        '',
            error:       '',
            focused:     false,
            highlighted: 0,
            menuStyle:   {}
        };
    },

    computed: {
        inputPlaceholder () {
            return this.chips.length && this.addPlaceholder ? this.addPlaceholder : this.placeholder;
        },

        chips () {
            return this.value || [];
        },

        query () {
            return this.text.trim();
        },

        isFull () {
            return this.max > 0 && this.chips.length >= this.max;
        },

        options () {
            const query    = this.query.toLowerCase();
            const selected = new Set(this.chips.map(chip => this.keyOf(chip)));
            let matches    = this.suggestions.filter(suggestion => !selected.has(this.keyOf(suggestion)));

            if (this.filter) {
                matches = matches
                    .filter(suggestion => this.lowerLabel(suggestion).includes(query))
                    .sort((first, second) => Number(!this.lowerLabel(first).startsWith(query)) - Number(!this.lowerLabel(second).startsWith(query)));
            }

            const options = matches
                .slice(0, MAX_OPTIONS)
                .map(item => ({
                    item,
                    label:  this.labelOf(item),
                    create: false
                }));

            const exists = [...this.suggestions, ...this.chips].some(item => this.lowerLabel(item) === query);

            if (this.allowCreate && query && !exists) {
                options[this.createFirst ? 'unshift' : 'push']({
                    item:   null,
                    label:  this.query,
                    create: true
                });
            }

            return options;
        },

        isOpen () {
            const hasSuggestions = this.filter ? this.suggestions.length > 0 : this.query !== '';

            return this.focused && !this.isFull && hasSuggestions &&
                (this.loading || this.options.length > 0) &&
                (this.query !== '' || this.filter);
        }
    },

    watch: {
        isOpen (open) {
            const method = open ? 'addEventListener' : 'removeEventListener';

            window[method]('scroll', this.placeMenu, true);
            window[method]('resize', this.placeMenu);

            if (open) {
                this.placeMenu();
            }
        }
    },

    updated () {
        if (this.isOpen) {
            this.placeMenu();
        }
    },

    beforeDestroy () {
        window.removeEventListener('scroll', this.placeMenu, true);
        window.removeEventListener('resize', this.placeMenu);
    },

    methods: {
        placeMenu () {
            const rect       = this.$refs.field.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove  = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow;
            const style      = {
                left:  `${rect.left}px`,
                width: `${rect.width}px`
            };

            if (openAbove) {
                style.bottom = `${window.innerHeight - rect.top + MENU_GAP}px`;
            } else {
                style.top = `${rect.bottom + MENU_GAP}px`;
            }

            if (JSON.stringify(style) !== JSON.stringify(this.menuStyle)) {
                this.menuStyle = style;
            }
        },

        labelOf (item) {
            return this.itemLabel(item);
        },

        lowerLabel (item) {
            return String(this.labelOf(item)).toLowerCase();
        },

        keyOf (item) {
            return this.itemKey ? this.itemKey(item) : this.lowerLabel(item);
        },

        type (value) {
            this.text        = value;
            this.highlighted = 0;
            this.error       = '';

            if (this.separator && value.includes(this.separator)) {
                const parts     = value.split(this.separator);
                const remainder = parts.pop().trimStart();

                parts.forEach(part => this.commit(part));
                this.text = remainder;
            }

            this.$emit('search', this.query);
        },

        close () {
            this.text = '';
            this.$refs.input.blur();
        },

        blur () {
            this.focused = false;

            if (this.commitOnBlur) {
                this.commit(this.text);
            }
        },

        move (step) {
            if (!this.isOpen || !this.options.length) {
                return;
            }

            this.highlighted = (this.highlighted + step + this.options.length) % this.options.length;
        },

        enter () {
            if (this.isOpen && !this.loading && this.options[this.highlighted]) {
                this.pick(this.highlighted);
                return;
            }

            this.commit(this.text);
        },

        pick (index) {
            const option = this.options[index];

            if (!option) {
                return;
            }

            if (option.create) {
                this.commit(option.label);
                return;
            }

            this.add(option.item);
        },

        commit (value) {
            const text = value.trim();

            if (!text) {
                return;
            }

            const match = this.suggestions.find(suggestion => this.lowerLabel(suggestion) === text.toLowerCase());

            if (match) {
                this.add(match);
                return;
            }

            if (!this.allowCreate) {
                return;
            }

            if (!this.parse) {
                this.add(text);
                return;
            }

            const result = this.parse(text);

            if (result.error) {
                this.error = result.error;
                return;
            }

            this.add(result.value);
        },

        add (item) {
            this.text  = '';
            this.error = '';

            if (this.isFull || this.chips.some(chip => this.keyOf(chip) === this.keyOf(item))) {
                return;
            }

            this.$emit('input', [...this.chips, item]);
            this.$emit('add', item);
        },

        remove (index) {
            const chip = this.chips[index];

            this.$emit('input', this.chips.filter((_, position) => position !== index));
            this.$emit('remove', chip, index);
        }
    }
};
</script>
